import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";

interface Transaction {
  id: string;
  description: string;
  customerId: string;
  point: number;
  type: "notification" | "point" | "reward" | "signup";
  createdAt: string;
}

interface TableProps {
  customerId?: string;
  passTemplateId?: string;
}

const typeBadge = (type: string, label: string) => {
  const styles: Record<string, string> = {
    point:        "bg-brand-50 text-brand-700",
    reward:       "bg-emerald-50 text-emerald-700",
    signup:       "bg-violet-50 text-violet-700",
    notification: "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[type] ?? "bg-gray-100 text-gray-600"}`}>
      {label}
    </span>
  );
};

const PAGE_SIZE = 20;

const TransactionTable: React.FC<TableProps> = ({ customerId, passTemplateId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!customerId && !passTemplateId) return;
      setLoading(true);
      setError(null);
      try {
        const url = customerId
          ? `/transactions/customer/${customerId}`
          : `/transactions/Passtemplate/${passTemplateId}`;

        const response = await OrganizationAxiosInstance.get<Transaction[]>(url, {
          params: { page, pageSize: PAGE_SIZE }
        });

        if (response.status === 200) {
          setTransactions(response.data);
          const total = parseInt(response.headers["x-total-count"] ?? "0");
          setTotalCount(total);
        }
      } catch (error) {
        if (isAxiosError(error)) {
          setError(error.status === 404 ? "No Transactions Found" : "Failed to fetch transactions.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [page, customerId, passTemplateId]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "notification": return t("sentNotificationToCustomerType");
      case "point":        return t("pointsEacrned");
      case "reward":       return t("rewardRadeemedType");
      case "signup":       return t("signedUpType");
      default:             return type;
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) return <p className="px-6 py-8 text-sm text-gray-400 text-center">Loading...</p>;
  if (error)   return <p className="px-6 py-8 text-sm text-gray-400 text-center">{error}</p>;
  if (transactions.length === 0) return <p className="px-6 py-8 text-sm text-gray-400 text-center">No transactions yet.</p>;

  return (
    <div>
      {/* Mobile */}
      <div className="sm:hidden divide-y divide-gray-100">
        {transactions.map(tx => (
          <div key={tx.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              {typeBadge(tx.type, getTypeLabel(tx.type))}
              <span className={`text-sm font-bold ${tx.point >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {tx.point >= 0 ? `+${tx.point}` : tx.point}
              </span>
            </div>
            <p className="text-sm text-gray-800">{tx.description}</p>
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{new Date(tx.createdAt).toLocaleDateString()}</span>
              <Link
                to={`/Dashboard/Program/${passTemplateId}/Customer/${tx.customerId}`}
                className="text-brand-600 font-medium hover:underline"
              >
                {t("customerDetails")}
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop */}
      <table className="hidden sm:table min-w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("date")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("customer")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("description")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("type")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("points")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {transactions.map(tx => (
            <tr key={tx.id} className="hover:bg-brand-50/40 transition">
              <td className="px-6 py-4 text-xs text-gray-500 whitespace-nowrap">
                {new Date(tx.createdAt).toLocaleString("en-US", {
                  year: "numeric", month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit", hour12: true,
                })}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Link
                  to={`/Dashboard/Program/${passTemplateId}/Customer/${tx.customerId}`}
                  className="text-sm text-brand-600 hover:underline font-medium"
                >
                  {t("customerDetails")}
                </Link>
              </td>
              <td className="px-6 py-4 text-sm text-gray-800">{tx.description}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                {typeBadge(tx.type, getTypeLabel(tx.type))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`text-sm font-bold ${tx.point >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {tx.point >= 0 ? `+${tx.point}` : tx.point}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
